"use client";

import { useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { t } from "@/lib/localization";
import { useLocale } from "@/hooks/useLocale";
import { useCreatePhotoAvatar } from "../../api/useCreatePhotoAvatar";
import { useCreateVideoAvatar } from "../../api/useCreateVideoAvatar";
import { useCreatePromptAvatar } from "../../api/useCreatePromptAvatar";
import Step1ChooseMode from "./step1ChooseMode";
import Step2ProvideMedia from "./step2ProvideMedia";
import Step3Name from "./step3Name";
import Step4Review from "./step4Review";
import { INITIAL_STATE, type WizardState } from "./types";

interface Props {
    onClose: () => void;
}

const TOTAL_STEPS = 4;

export default function CreateAvatarWizard({ onClose }: Props) {
    const { locale, dir } = useLocale();
    const isRtl = dir === "rtl";
    const BackIcon = isRtl ? ChevronRight : ChevronLeft;
    const NextIcon = isRtl ? ChevronLeft : ChevronRight;
    const [state, setState] = useState<WizardState>(INITIAL_STATE);
    const [step, setStep] = useState(0);

    const photo = useCreatePhotoAvatar();
    const video = useCreateVideoAvatar();
    const prompt = useCreatePromptAvatar();
    const submitting = photo.isPending || video.isPending || prompt.isPending;

    const update = (patch: Partial<WizardState>) =>
        setState((s) => ({ ...s, ...patch }));

    const canAdvance = () => {
        if (step === 0) return state.mode !== null;
        if (step === 1) {
            if (state.mediaError) return false;
            if (state.mode === "prompt") return state.prompt.trim().length > 0;
            return !!state.assetId || !!state.url;
        }
        if (step === 2) return state.name.trim().length > 0;
        return true;
    };

    const handleSubmit = async () => {
        try {
            if (state.mode === "photo") {
                await photo.mutateAsync({
                    name: state.name,
                    assetId: state.assetId,
                    url: state.url,
                });
            } else if (state.mode === "video") {
                const res = await video.mutateAsync({
                    name: state.name,
                    assetId: state.assetId,
                    url: state.url,
                    rerouteUrl:
                        typeof window !== "undefined"
                            ? window.location.href
                            : undefined,
                });
                if (res.data.consentUrl) {
                    window.open(
                        res.data.consentUrl,
                        "_blank",
                        "noopener,noreferrer"
                    );
                }
            } else if (state.mode === "prompt") {
                await prompt.mutateAsync({
                    name: state.name,
                    prompt: state.prompt,
                    referenceImages: state.referenceImages.map((img) => ({
                        assetId: img.assetId,
                        url: img.url,
                    })),
                });
            }
            onClose();
        } catch {
            // Mutation hooks already surface error toasts. Stay on the review
            // step so the user can try again.
        }
    };

    return (
        <div className="flex flex-col gap-5">
            <StepHeader step={step} mode={state.mode} />

            {step === 0 && (
                <Step1ChooseMode
                    selected={state.mode}
                    onSelect={(m) => update({ mode: m })}
                />
            )}
            {step === 1 && <Step2ProvideMedia state={state} update={update} />}
            {step === 2 && <Step3Name state={state} update={update} />}
            {step === 3 && <Step4Review state={state} />}

            <div className="flex justify-between gap-2 pt-2">
                <Button
                    variant="ghost"
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0 || submitting}
                >
                    <BackIcon className="size-4 me-1" />
                    {t("avatars.wizard.back", locale)}
                </Button>
                {step < TOTAL_STEPS - 1 ? (
                    <Button
                        onClick={() => setStep((s) => s + 1)}
                        disabled={!canAdvance() || submitting}
                    >
                        {t("avatars.wizard.next", locale)}
                        <NextIcon className="size-4 ms-1" />
                    </Button>
                ) : (
                    <Button
                        onClick={handleSubmit}
                        disabled={submitting || !canAdvance()}
                    >
                        {submitting && (
                            <Loader2 className="size-4 me-2 animate-spin" />
                        )}
                        {state.mode === "video"
                            ? t("avatars.wizard.submit-video", locale)
                            : t("avatars.wizard.create", locale)}
                    </Button>
                )}
            </div>
        </div>
    );
}

function StepHeader({
    step,
    mode,
}: {
    step: number;
    mode: WizardState["mode"];
}) {
    const { locale } = useLocale();
    const titleKey =
        step === 0
            ? "avatars.wizard.step1.title"
            : step === 1
              ? mode === "prompt"
                  ? "avatars.wizard.step2.title.prompt"
                  : mode === "video"
                    ? "avatars.wizard.step2.title.video"
                    : "avatars.wizard.step2.title.photo"
              : step === 2
                ? "avatars.wizard.step3.title"
                : "avatars.wizard.step4.title";
    const descKey =
        step === 0
            ? "avatars.wizard.step1.subtitle"
            : step === 1
              ? mode === "prompt"
                  ? "avatars.wizard.step2.subtitle.prompt"
                  : mode === "video"
                    ? "avatars.wizard.step2.subtitle.video"
                    : "avatars.wizard.step2.subtitle.photo"
              : step === 2
                ? "avatars.wizard.step3.subtitle"
                : "avatars.wizard.step4.subtitle";
    return (
        <DialogHeader className="space-y-0 gap-1">
            <StepIndicator current={step} total={TOTAL_STEPS} />
            <DialogTitle>{t(titleKey, locale)}</DialogTitle>
            <DialogDescription>{t(descKey, locale)}</DialogDescription>
        </DialogHeader>
    );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
    return (
        <div
            className="mb-4 flex gap-1.5 px-0.5"
            aria-label={`Step ${current + 1} of ${total}`}
        >
            {Array.from({ length: total }).map((_, i) => {
                const active = i <= current;
                return (
                    <div
                        key={i}
                        className={
                            active
                                ? "h-1.5 flex-1 bg-primary rounded-full transition-colors"
                                : "h-1.5 flex-1 bg-secondary rounded-full transition-colors"
                        }
                    />
                );
            })}
        </div>
    );
}
